import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, EmailGuard } from "../App";
import { Oval } from "react-loader-spinner";
import { FaRecycle, FaTrash, FaChevronLeft, FaChevronRight, FaDollarSign, FaHome } from 'react-icons/fa';
import { StandardURL } from "../App";

const AllTrashFeesPage = () => {
    const [trashFees, setTrashFees] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
    const [propertyIdToDelete, setPropertyIdToDelete] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const feesPerPage = 10;
    
    const navigate = useNavigate();

    // --- Data Fetching ---
    const fetchTrashFees = () => {
        const token = localStorage.getItem("token");
        setIsLoading(true);
        axios
            .get(`${StandardURL}/iNyumba/trashfee/fetchalltrashfee`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })
            .then((response) => {
                const fetchedFees = Array.isArray(response.data.response) ? response.data.response : [];
                setTrashFees(fetchedFees);
                if (fetchedFees.length > 0) {
                    toast.success("Trash fees data fetched successfully!");
                } else {
                    toast.info("No trash fees found.");
                }
                setIsLoading(false);
            })
            .catch((error) => {
                toast.error(
                    error.response?.data?.response || "Failed to fetch trash fees."
                );
                console.error("Error fetching trash fees:", error);
                setIsLoading(false);
            });
    };

    useEffect(() => {
        fetchTrashFees();
    }, []);

    // --- Delete Logic ---
    const confirmDelete = (propertyId) => {
        setPropertyIdToDelete(propertyId);
        setIsDeleteConfirmModalOpen(true);
    };

    const handleDeleteTrashFee = () => {
        const propertyId = propertyIdToDelete;
        if (!propertyId) return;

        const token = localStorage.getItem("token");
        setIsLoading(true);
        setIsDeleteConfirmModalOpen(false); // Close modal right away
        
        axios
            .delete(
                `${StandardURL}/iNyumba/trashfee/deletetrashfee/${propertyId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            )
            .then((response) => {
                toast.success(response.data.response || `Trash fee for Property ID ${propertyId} deleted successfully.`);
                setTrashFees(trashFees.filter((fee) => fee.propertyid !== propertyId));
                setIsLoading(false);
                setPropertyIdToDelete(null);
                setCurrentPage(1); // Reset to first page
            })
            .catch((error) => {
                toast.error(
                    error.response?.data?.response || "Failed to delete trash fee."
                );
                console.error("Error deleting trash fee:", error);
                setIsLoading(false);
                setPropertyIdToDelete(null);
            });
    };

    // --- Pagination Logic ---
    const indexOfLastFee = currentPage * feesPerPage;
    const indexOfFirstFee = indexOfLastFee - feesPerPage;
    const currentFees = trashFees.slice(indexOfFirstFee, indexOfLastFee);
    const totalPages = Math.ceil(trashFees.length / feesPerPage);

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const nextPage = () => paginate(currentPage + 1);
    const prevPage = () => paginate(currentPage - 1);

    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
    }
    
    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8">
                    <div className="max-w-6xl mx-auto">
                        <h1 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                            <FaRecycle className="mr-3 text-indigo-600" /> Manage Trash Fees
                        </h1>

                        {/* Loading Spinner */}
                        {isLoading && (
                            <div className="flex justify-center items-center py-10">
                                <Oval color="#4f46e5" secondaryColor="#6366f1" height={50} width={50} ariaLabel="loading-spinner" />
                            </div>
                        )}

                        {/* Trash Fees Table Card */}
                        {!isLoading && (
                            <div className="bg-white shadow-xl rounded-xl overflow-hidden">
                                <div className="p-4 bg-indigo-50 border-b border-indigo-100">
                                    <h2 className="text-xl font-semibold text-gray-800">Fee Records ({trashFees.length})</h2>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-100">
                                            <tr>
                                                <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center">
                                                    <FaHome className="mr-1"/> Property ID
                                                </th>
                                                <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    <FaDollarSign className="mr-1 inline"/> Fee Amount
                                                </th>
                                                <th className="py-3 px-6 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {currentFees.length > 0 ? (
                                                currentFees.map((trashFee) => (
                                                    <tr key={trashFee.propertyid} className="hover:bg-red-50 transition duration-150">
                                                        <td className="py-4 px-6 whitespace-nowrap text-sm font-medium text-gray-900">{trashFee.propertyid}</td>
                                                        <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-700 font-bold">KES {parseFloat(trashFee.feeamount).toFixed(2)}</td>
                                                        <td className="py-4 px-6 whitespace-nowrap text-sm font-medium">
                                                            <button
                                                                onClick={() => confirmDelete(trashFee.propertyid)}
                                                                className="inline-flex items-center bg-red-600 text-white py-1.5 px-3 rounded-lg hover:bg-red-700 transition duration-150 shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                            >
                                                                <FaTrash className="mr-1.5 text-xs" /> Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="3" className="py-8 text-center text-gray-500">
                                                        No trash fees available for the current selection.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination Controls */}
                                {totalPages > 1 && (
                                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                            <div>
                                                <p className="text-sm text-gray-700">
                                                    Showing <span className="font-medium">{indexOfFirstFee + 1}</span> to <span className="font-medium">{Math.min(indexOfLastFee, trashFees.length)}</span> of <span className="font-medium">{trashFees.length}</span> results
                                                </p>
                                            </div>
                                            <div>
                                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                                    <button
                                                        onClick={prevPage}
                                                        disabled={currentPage === 1}
                                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                    >
                                                        <FaChevronLeft className="h-5 w-5" aria-hidden="true" />
                                                    </button>
                                                    {pageNumbers.map(number => (
                                                        <button
                                                            key={number}
                                                            onClick={() => paginate(number)}
                                                            aria-current={number === currentPage ? 'page' : undefined}
                                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                                number === currentPage
                                                                    ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                            }`}
                                                        >
                                                            {number}
                                                        </button>
                                                    ))}
                                                    <button
                                                        onClick={nextPage}
                                                        disabled={currentPage === totalPages}
                                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                                    >
                                                        <FaChevronRight className="h-5 w-5" aria-hidden="true" />
                                                    </button>
                                                </nav>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                {isDeleteConfirmModalOpen && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full flex items-center justify-center z-50">
                        <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full transform transition-all">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center border-b pb-2">
                                <FaTrash className="mr-2 text-red-600"/> Confirm Deletion
                            </h3>
                            <div className="mt-4">
                                <p className="text-sm text-gray-700">
                                    Are you sure you want to permanently delete the trash fee for **Property ID: {propertyIdToDelete}**? This action cannot be undone.
                                </p>
                            </div>
                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    onClick={() => setIsDeleteConfirmModalOpen(false)}
                                    type="button"
                                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition duration-150"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDeleteTrashFee}
                                    type="button"
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-150"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </AuthGuard>
        </EmailGuard>
    );
};

export default AllTrashFeesPage;